import { ArrowRight, Trophy } from "lucide-react";
import { Reveal, SectionHead, CTAButton, PageHero, Marquee } from "@/components/bits";
import { IMAGES, ASSETS, DIFFERENTIATORS } from "@/data/site";
import * as Icons from "lucide-react";

const About = () => (
  <>
    <PageHero
      eyebrow="About Us · 30+ Years of Legacy"
      title={<>Three decades of discipline, training &amp; <span className="text-red-500">performance</span></>}
      sub="New Boogie Woogie Academy is a vibrant and dedicated learning space where passion, discipline, creativity, and performance come together."
      img={ASSETS.studio1}
    />

    <section className="mx-auto max-w-[1440px] px-5 pt-4 sm:px-8" data-testid="about-studio-photos">
      <div className="grid gap-4 sm:grid-cols-2">
        <Reveal className="img-zoom overflow-hidden border border-white/10">
          <img src={ASSETS.studio2} alt="Mirrored training hall at New Boogie Woogie Academy" className="aspect-[16/10] w-full object-cover" loading="lazy" />
        </Reveal>
        <Reveal delay={0.1} className="img-zoom overflow-hidden border border-white/10">
          <img src={ASSETS.studio3} alt="Signature neon logo wall at New Boogie Woogie Academy" className="aspect-[16/10] w-full object-cover" loading="lazy" />
        </Reveal>
      </div>
      <p className="mt-4 font-mono2 text-[10px] uppercase tracking-[0.24em] text-zinc-500">Our own studio — real photographs from the academy</p>
    </section>

    <section className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8" data-testid="about-story">
      <div className="grid gap-14 lg:grid-cols-2">
        <Reveal>
          <h2 className="font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">Our Story</h2>
          <p className="mt-6 text-sm leading-relaxed text-zinc-400 sm:text-base">
            Our academy is committed to helping students discover their potential through quality training,
            structured classes, professional guidance, and a positive learning environment.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400 sm:text-base">
            Whether a student is taking their first steps or looking to develop their existing skills, we provide
            an environment where they can learn with confidence and grow at their own pace.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400 sm:text-base">
            At New Boogie Woogie Academy, every class is an opportunity to learn something new, build confidence,
            and enjoy the journey of becoming a better performer.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <h2 className="font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">Our Mission</h2>
          <p className="mt-6 text-sm leading-relaxed text-zinc-400 sm:text-base">
            We believe that true learning goes beyond techniques. Through regular practice and consistent
            guidance, we encourage students to develop discipline, confidence, coordination, creativity, teamwork,
            stage presence, and self-expression.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400 sm:text-base">
            Our teachers value punctuality, consistency, and student development. We strive to conduct every class
            in an organized manner and remain available to guide students throughout their learning journey.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["Discipline", "Confidence", "Coordination", "Creativity", "Teamwork", "Stage Presence", "Self-Expression"].map((v) => (
              <span key={v} className="border border-white/12 px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-zinc-300">
                {v}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>

    <Marquee items={["Learn with Discipline", "Practice with Dedication", "Perform with Confidence", "Grow with Passion"]} />

    <section className="mx-auto max-w-[1440px] px-5 py-24 sm:px-8" data-testid="about-values">
      <SectionHead eyebrow="What We Stand For" title={<>The values behind <span className="text-red-500">every class</span></>} />
      <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {DIFFERENTIATORS.map((d, i) => {
          const Icon = Icons[d.icon] || Icons.Sparkles;
          return (
            <Reveal key={d.title} delay={(i % 4) * 0.07}>
              <article className="card-glow h-full border border-white/10 bg-[#0F0F14] p-7">
                <Icon size={24} className="text-red-500" aria-hidden="true" />
                <h3 className="mt-5 font-display text-sm font-extrabold uppercase tracking-wide text-white">{d.title}</h3>
                <p className="mt-3 text-[13px] leading-relaxed text-zinc-400">{d.desc}</p>
              </article>
            </Reveal>
          );
        })}
      </div>
    </section>

    <section className="border-y border-white/10 bg-[#08080B] py-24" data-testid="awards-preview">
      <div className="mx-auto max-w-[1440px] px-5 text-center sm:px-8">
        <Reveal>
          <Trophy size={34} className="mx-auto text-red-500" aria-hidden="true" />
          <h2 className="mt-6 font-display text-3xl font-black uppercase tracking-tight sm:text-4xl">Our Journey of Recognition</h2>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-zinc-400">
            Award and recognition photographs from the academy's 30+ year journey will be showcased here as they
            are added by the academy.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-[1440px] px-5 py-24 text-center sm:px-8" data-testid="about-cta">
      <Reveal>
        <h2 className="font-display text-3xl font-black uppercase tracking-tight sm:text-5xl">
          Start <span className="text-red-500">your</span> journey
        </h2>
        <p className="mx-auto mt-5 max-w-lg text-sm text-zinc-400 sm:text-base">
          Book an appointment or send an enquiry — our team will help you find the right class.
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <CTAButton href="/booking" testid="about-start-btn">Start Your Journey <ArrowRight size={15} /></CTAButton>
          <CTAButton href="/activities" variant="ghost" testid="about-activities-btn">Explore Activities</CTAButton>
        </div>
      </Reveal>
    </section>
  </>
);

export default About;
